import type { AiSearchApiResult } from './api';

export type AiSearchJobPayload = {
  message_id: string;
  status: string;
};

export const isAiSearchJobPayload = (value: unknown): value is AiSearchJobPayload => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.message_id === 'string' &&
    record.message_id.trim().length > 0 &&
    typeof record.status === 'string' &&
    record.status.trim().length > 0
  );
};

export const formatApiError = (response: Record<string, unknown> | null | undefined): string => {
  if (!response) {
    return 'Request failed';
  }

  const status = typeof response.status === 'number' ? response.status : undefined;
  const errorCode = typeof response.error_code === 'string' ? response.error_code : '';

  if (status === 402) {
    return 'Insufficient credits';
  }
  if (status === 401) {
    return 'Please sign in again';
  }
  if (errorCode.startsWith('STAGE1_')) {
    return 'Could not understand your question. Please try again.';
  }

  const message = response.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }
  if (message && typeof message === 'object' && 'error' in (message as object)) {
    const nested = (message as { error?: { message?: string } }).error;
    if (nested?.message) {
      return nested.message;
    }
  }
  const err = response.error;
  if (typeof err === 'string') {
    return err;
  }
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: string }).message === 'string'
  ) {
    return (err as { message: string }).message;
  }
  if (status) {
    return `Request failed (${status})`;
  }
  return 'Request failed';
};

export const parseAiSearchResponse = <T extends AiSearchJobPayload>(
  response: unknown
): AiSearchApiResult<T> => {
  if (!response || typeof response !== 'object') {
    return { ok: false, error: 'Request failed' };
  }

  const record = response as Record<string, unknown>;
  const status = typeof record.status === 'number' ? record.status : undefined;

  if (status !== undefined && status >= 400) {
    return {
      ok: false,
      error: formatApiError(record),
      status,
    };
  }

  const message = record.message;
  if (isAiSearchJobPayload(message)) {
    return { ok: true, data: message as T };
  }

  return {
    ok: false,
    error: formatApiError(record),
    status,
  };
};
